import type {SafeHTMLString} from '@github-ui/safe-html'

/**
 * Prevents image flickering in rendered markdown by preserving token URLs across re-renders.
 * When URLs with tokens change during re-renders, browsers treat them as new resources,
 * causing images to reload unnecessarily. This utility maintains the same tokens
 * while allowing other content to update normally.
 */

// Maximum number of iterations to prevent regex DOS attacks
const MAX_REGEX_ITERATIONS = 1000

// Shared regex to extract href/src attributes with a token parameter in any position in the query string
// Group 1: attribute name (href or src)
// Group 2: quote style (' or ")
// Group 3: base URL (everything before the query string)
// Group 4: query string portion before token (includes the leading ?)
// Group 5: token value
// Group 6: query string portion after token (may be empty)
// Using length limits to prevent catastrophic backtracking
const URL_TOKEN_REGEX =
  /\b(href|src)\s*=\s*(['"])([^'"?]{1,2000})(\?(?:[^'"]{0,1000}?))token=([^&#"']{0,500})([^'"]{0,1000})\2/gi

/**
 * Replaces all content in html1 with html2, EXCEPT for ?token=... query parameter values
 * in href/src attributes, which are preserved from html1 IF the url (without the token) matches.
 * For a match to occur, both the base URL and all non-token query parameters must be identical.
 *
 * @param {string} html1 - The original HTML (tokens preserved from here)
 * @param {string} html2 - The new HTML (all else taken from here)
 * @returns {string} - The merged HTML
 */
export function mergeHtmlPreserveTokensByUrl(html1: SafeHTMLString, html2: SafeHTMLString): SafeHTMLString {
  const tokenMap = extractTokens(html1)
  return injectTokens(html2, tokenMap)
}

// Helper: extract a map of URL key => token from html1
// The URL key includes the base URL and query params (excluding the token value)
function extractTokens(html: SafeHTMLString) {
  const tokenMap: Record<string, string> = {}

  // Reset regex state before use
  URL_TOKEN_REGEX.lastIndex = 0

  let match
  let iterationCount = 0
  while ((match = URL_TOKEN_REGEX.exec(html)) !== null) {
    // Safety check to prevent infinite loops or excessive processing
    if (++iterationCount > MAX_REGEX_ITERATIONS) {
      // Abort to prevent potential regex DOS attack
      break
    }

    const baseUrl = match[3]
    const beforeToken = match[4] // includes the leading ?
    const token = match[5]
    const afterToken = match[6]

    if (!baseUrl || !token) continue

    // Create a key that includes all query parameters except the token value
    // This way we only match URLs with identical query parameters
    const urlKey = `${baseUrl}${beforeToken}token=TOKEN_PLACEHOLDER${afterToken}`
    tokenMap[urlKey] = token
  }
  return tokenMap
}

// Replace tokens in html2 with those from html1 only if the complete URL (except token) matches
function injectTokens(html: SafeHTMLString, tokenMap: Record<string, string>): SafeHTMLString {
  // Reset regex state before use
  URL_TOKEN_REGEX.lastIndex = 0

  // Use a safer replace approach by limiting the amount of computation
  let iterationCount = 0
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized segments */
  const result = html.replace(URL_TOKEN_REGEX, (match, attr, quote, baseUrl, beforeToken, token, afterToken) => {
    // Safety check to prevent excessive computation
    if (++iterationCount > MAX_REGEX_ITERATIONS) {
      return match // Return unchanged if we hit the limit
    }

    // Create the same key format we used in extractTokens
    const urlKey = `${baseUrl}${beforeToken}token=TOKEN_PLACEHOLDER${afterToken}`

    // Check if we have this exact URL pattern (with same query params) in our token map
    if (tokenMap.hasOwnProperty(urlKey)) {
      // Replace only the token value, preserving everything else
      return `${attr}=${quote}${baseUrl}${beforeToken}token=${tokenMap[urlKey]}${afterToken}${quote}`
    }
    // No preserved token for this URL, leave unchanged
    return match
  }) as SafeHTMLString

  return result
}
